import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Form, Input } from 'antd';

import { ROUTE_URLS } from '../../routes';
import { useUpdateUser, useUser } from '../../hooks';
import { Card, FormActions, FormSkeleton, PageHeader } from '../../components';
import { useI18n } from '../../i18n';
import { authReducerActions } from '../../store/reducers';
import { getFirstAllowedPath } from '../../navigation';
import { PagesCard } from './PagesCard';

export const User = () => {
  const { user, isAdmin, pages } = useSelector(state => state.authReducer);
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useI18n();
  const isSelf = user.id.toString() === id;
  const backTo = isAdmin ? ROUTE_URLS.USERS : getFirstAllowedPath(isAdmin, pages) ?? ROUTE_URLS.HOME;
  const { isLoading, mutateAsync: updateUser } = useUpdateUser({
    redirectTo: backTo,
    onSuccess: values => {
      if (isSelf) dispatch(authReducerActions.updateProfile({ login: values.login, email: values.email }));
    },
  });
  const { data: userInfo, isLoading: isUserDataLoading } = useUser(id);
  const [form] = Form.useForm();
  const emailValue = Form.useWatch('email', form);
  const isEmailMissing = !isUserDataLoading && !(emailValue ?? userInfo?.email)?.trim();
  const canEditPages = isAdmin && !isSelf && Array.isArray(userInfo?.pages);

  useEffect(() => {
    if (!isAdmin && !isSelf) {
      navigate(ROUTE_URLS.HOME);
    }
  }, [isAdmin, isSelf, navigate]);

  const onFinish = ({ password, ...values }) => {
    updateUser({
      id,
      ...values,
      email: values.email || null,
      ...(password ? { password } : {}),
    });
  };

  return (
    <div className='wm-form-page wm-form-page--narrow'>
      <PageHeader
        backTo={backTo}
        backLabel={isAdmin ? t('nav.users') : t('nav.home')}
        title={t('users.editTitle')}
        subtitle={userInfo?.login ?? t('users.editSubtitle')}
      />
      {isEmailMissing && (
        <Alert
          type='warning'
          showIcon
          message={t('users.emailMissingTitle')}
          description={t('users.emailMissingText')}
          style={{ marginBottom: 16 }}
        />
      )}
      {isUserDataLoading ? (
        <Card>
          <FormSkeleton fields={3} />
        </Card>
      ) : (
        <Form
          form={form}
          name='fields'
          layout='vertical'
          onFinish={onFinish}
          initialValues={{
            login: userInfo?.login,
            email: userInfo?.email,
            ...(canEditPages ? { pages: userInfo.pages } : {}),
          }}
        >
          <Card>
            <div className='wm-profile-head'>
              <span className='wm-avatar wm-avatar--lg'>{(userInfo?.login ?? '?').slice(0, 1).toUpperCase()}</span>
              <div>
                <div className='wm-strong'>{userInfo?.login}</div>
                <div className='wm-muted'>{t('users.editSubtitle')}</div>
              </div>
            </div>
            <Form.Item
              label={t('users.login')}
              name='login'
              rules={[
                { required: true, message: t('validation.required', { field: t('users.login') }) },
                { min: 4, message: t('users.loginMin') },
              ]}
            >
              <Input placeholder={t('users.loginPlaceholder')} autoComplete='username' />
            </Form.Item>
            <Form.Item
              label={t('users.email')}
              name='email'
              extra={t('users.emailHint')}
              rules={[{ type: 'email', message: t('users.emailInvalid') }]}
            >
              <Input placeholder={t('users.emailPlaceholder')} autoComplete='email' />
            </Form.Item>
            <Form.Item
              label={t('users.password')}
              name='password'
              extra={t('users.newPasswordHint')}
              rules={[{ min: 4, message: t('users.passwordMin') }]}
              style={{ marginBottom: 4 }}
            >
              <Input.Password placeholder={t('users.passwordPlaceholder')} autoComplete='new-password' />
            </Form.Item>
          </Card>
          {canEditPages && <PagesCard />}
          <FormActions cancelTo={backTo} submitting={isLoading} />
        </Form>
      )}
    </div>
  );
};
